import { AnimatePresence, motion } from 'motion/react'
import { createContext, useCallback, useContext, useRef, useState } from 'react'
import type { PointerEvent, ReactNode } from 'react'

import { FOLLOW_SPRING, PILL_SPRING } from '@/lib/motion'
import { cn } from '@/lib/utils'

/*
 * Two pieces of motion every list of choices shares — the sidebar's rail and
 * panel, and tab strips:
 *
 * - A hover highlight that glides from item to item after the pointer, one
 *   element for the whole list rather than a fill per item, so moving down a
 *   list reads as one motion instead of a flicker.
 * - An active pill that slides to whichever item is chosen.
 *
 * Both sit behind the item's content: the item is `relative`, and anything
 * inside it that should draw above them is `relative` too.
 *
 * The hover highlight is rendered as the area's *first* child while it shows,
 * so nothing directly inside a `GlideArea` can style itself by position
 * (`first:`, `odd:`…): the first hover would restyle it, and the list jumps.
 */

type Box = { x: number; y: number; width: number; height: number }

const GlideContext = createContext<((element: HTMLElement) => void) | null>(null)

export function GlideArea({
  children,
  className,
  highlightClassName,
}: {
  children: ReactNode
  className?: string
  /** The highlight's fill and radius, e.g. `bg-sidebar-hover rounded-md`. */
  highlightClassName: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<Box | null>(null)

  const show = useCallback((element: HTMLElement) => {
    const area = ref.current
    if (!area) return
    const outer = area.getBoundingClientRect()
    const inner = element.getBoundingClientRect()
    setBox({
      x: inner.left - outer.left + area.scrollLeft,
      y: inner.top - outer.top + area.scrollTop,
      width: inner.width,
      height: inner.height,
    })
  }, [])

  return (
    <div
      ref={ref}
      className={cn('relative', className)}
      // Gone when the pointer leaves, and when the list scrolls or the page
      // changes under a still pointer: a highlight left on the row the
      // pointer used to be over reads as a second selection.
      onPointerLeave={() => setBox(null)}
      onScrollCapture={() => setBox(null)}
      onClickCapture={() => setBox(null)}
    >
      <AnimatePresence>
        {box && (
          <motion.div
            aria-hidden="true"
            className={cn('pointer-events-none absolute top-0 left-0', highlightClassName)}
            // Appears where the pointer is rather than flying in from the
            // corner: the first frame starts at the box, only faded out.
            initial={{ opacity: 0, ...box }}
            animate={{ opacity: 1, ...box }}
            exit={{ opacity: 0 }}
            transition={FOLLOW_SPRING}
          />
        )}
      </AnimatePresence>
      <GlideContext.Provider value={show}>{children}</GlideContext.Provider>
    </div>
  )
}

/**
 * The handler an item puts on `onPointerEnter`. Touch is skipped: a finger
 * has no hover, and the highlight would stay stuck where it last landed.
 */
export function useGlideTarget() {
  const show = useContext(GlideContext)
  return useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (event.pointerType !== 'touch') show?.(event.currentTarget)
    },
    [show],
  )
}

/**
 * The active item's pill. Render it inside the active item only; items
 * sharing a `layoutId` hand it from one to the next, which is the slide.
 */
export function ActivePill({ layoutId, className }: { layoutId: string; className: string }) {
  return (
    <motion.span
      aria-hidden="true"
      layoutId={layoutId}
      className={cn('absolute inset-0', className)}
      transition={PILL_SPRING}
    />
  )
}
